import type { UserRole } from "./RoleContext";
import { ROLE_PERMISSIONS, PERMISSIONS, hasPermission } from "./permissions";

/** Default landing page for each role */
export const ROLE_DEFAULT_ROUTES: Record<UserRole, string> = {
  super_admin: "/command-dashboard",
  owner: "/command-dashboard",
  cfo: "/commissions",
  finance_manager: "/commissions",
  bookkeeper: "/accounting",
  ops_manager: "/",
  admin: "/command-dashboard",
  warehouse_manager: "/",
  head_of_installation: "/",
  procurement_manager: "/",
  inventory_manager: "/",
  installer: "/",
  subcontractor: "/",
  sales: "/",
  sales_manager: "/command-dashboard",
  call_center: "/pipeline",
  it_staff: "/command-dashboard",
};

/**
 * Routes each role is allowed to access.
 * "/" (dashboard) is always allowed — the Dashboard component itself
 * renders the correct role-specific view.
 */
const ROLE_ALLOWED_ROUTES: Record<UserRole, string[]> = {
  super_admin: [
    "/", "/intake-review", "/pipeline", "/pipeline/:id", "/lead-sources", "/automations", "/lead-integrity",
    "/import/review-queue", "/import/leads-appointments",
    "/messages", "/team-messages", "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id", "/customers", "/customers/:name",
    "/scheduling", "/scheduling-dispatch", "/closeout", "/permitting", "/ordering-queue", "/install-readiness", "/warehouse", "/inventory",
    "/issues", "/service-tickets", "/contractor-intake", "/contractor-onboarding",
    "/contractors", "/contractors/:id", "/qa-form", "/issue-form",
    "/reports", "/accounting", "/marketing", "/marketing-attribution", "/growth-channels", "/disposition-report", "/alerts",
    "/system-audit", "/intelligence", "/portals", "/settings",
    "/offerings", "/follow-up-engine", "/product-registry", "/vendors",
    "/friction-log", "/earnings-intelligence", "/control-plane", "/demo-lab",
    "/revenue-intelligence", "/dispatch-intelligence", "/installer-dashboard",
    "/pipeline-intelligence", "/architecture-health",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/commissions",
    "/command-dashboard", "/navigation-settings",
  ],
  owner: [
    "/", "/intake-review", "/pipeline", "/pipeline/:id", "/lead-sources", "/automations", "/lead-integrity",
    "/import/review-queue", "/import/leads-appointments",
    "/messages", "/team-messages", "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id", "/customers", "/customers/:name",
    "/scheduling", "/scheduling-dispatch", "/closeout", "/permitting", "/ordering-queue", "/install-readiness",
    "/warehouse", "/inventory", "/vendors",
    "/issues", "/service-tickets",
    "/contractors", "/contractors/:id",
    "/reports", "/accounting", "/marketing", "/marketing-attribution", "/growth-channels", "/disposition-report", "/alerts", "/system-audit", "/intelligence",
    "/portals", "/settings", "/friction-log", "/earnings-intelligence", "/control-plane", "/demo-lab",
    "/revenue-intelligence", "/dispatch-intelligence", "/pipeline-intelligence", "/architecture-health",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/commissions",
    "/command-dashboard", "/navigation-settings",
  ],
  cfo: [
    "/", "/jobs", "/jobs/:id", "/opportunities", "/opportunities/:id", "/customers", "/customers/:name",
    "/accounting", "/reports", "/alerts", "/intelligence", "/earnings-intelligence", "/revenue-intelligence",
    "/commissions", "/team-messages",
  ],
  finance_manager: [
    "/", "/jobs", "/jobs/:id", "/opportunities", "/opportunities/:id", "/customers", "/customers/:name",
    "/accounting", "/reports", "/alerts", "/intelligence", "/earnings-intelligence", "/commissions",
    "/revenue-intelligence", "/team-messages",
  ],
  bookkeeper: [
    "/", "/jobs", "/jobs/:id", "/opportunities", "/opportunities/:id", "/customers", "/customers/:name",
    "/accounting", "/reports", "/alerts", "/intelligence",
    "/revenue-intelligence", "/earnings-intelligence", "/commissions", "/team-messages",
  ],
  ops_manager: [
    "/", "/intake-review", "/pipeline", "/pipeline/:id", "/lead-sources", "/automations", "/lead-integrity",
    "/import/review-queue", "/import/leads-appointments",
    "/messages", "/team-messages", "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id", "/customers", "/customers/:name",
    "/scheduling", "/scheduling-dispatch", "/closeout", "/permitting", "/ordering-queue", "/install-readiness", "/warehouse", "/inventory", "/vendors",
    "/issues", "/service-tickets", "/contractor-intake", "/contractor-onboarding",
    "/contractors", "/contractors/:id", "/qa-form", "/issue-form",
    "/reports", "/accounting", "/marketing", "/marketing-attribution", "/growth-channels", "/disposition-report", "/alerts", "/intelligence", "/revenue-intelligence", "/pipeline-intelligence",
    "/portals",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/commissions",
    "/command-dashboard",
  ],
  admin: [
    "/", "/intake-review", "/pipeline", "/pipeline/:id", "/lead-sources", "/automations", "/lead-integrity",
    "/import/review-queue", "/import/leads-appointments",
    "/messages", "/team-messages", "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id", "/customers", "/customers/:name",
    "/scheduling", "/scheduling-dispatch", "/closeout", "/permitting", "/ordering-queue", "/install-readiness", "/warehouse", "/inventory",
    "/issues", "/service-tickets", "/contractor-intake", "/contractor-onboarding",
    "/contractors", "/contractors/:id", "/qa-form", "/issue-form",
    "/reports", "/accounting", "/marketing", "/marketing-attribution", "/growth-channels", "/disposition-report", "/alerts", "/intelligence",
    "/portals",
    "/offerings", "/follow-up-engine", "/product-registry", "/vendors",
    "/friction-log", "/settings",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/commissions",
    "/command-dashboard", "/navigation-settings",
  ],
  warehouse_manager: [
    "/", "/warehouse", "/inventory", "/jobs", "/jobs/:id",
    "/issues", "/service-tickets", "/issue-form",
    "/ordering-queue", "/scheduling", "/team-messages",
  ],
  head_of_installation: [
    "/", "/warehouse", "/inventory", "/install-readiness", "/jobs", "/jobs/:id",
    "/scheduling", "/scheduling-dispatch", "/issues", "/service-tickets", "/issue-form",
    "/qa-form", "/contractors", "/contractors/:id",
    "/ordering-queue", "/team-messages",
  ],
  procurement_manager: [
    "/", "/ordering-queue", "/warehouse", "/inventory", "/jobs", "/jobs/:id", "/vendors",
    "/issues", "/service-tickets", "/scheduling", "/team-messages",
  ],
  inventory_manager: [
    "/", "/warehouse", "/inventory", "/jobs", "/jobs/:id", "/vendors",
    "/issues", "/service-tickets", "/scheduling", "/team-messages",
  ],
  installer: [
    "/", "/qa-form", "/issue-form", "/jobs", "/jobs/:id", "/installer-dashboard", "/scheduling", "/team-messages",
  ],
  subcontractor: [
    "/", "/jobs", "/jobs/:id", "/opportunities", "/opportunities/:id",
    "/qa-form", "/issue-form", "/scheduling", "/team-messages",
  ],
  sales: [
    "/", "/pipeline", "/pipeline/:id", "/messages", "/team-messages", "/opportunities", "/opportunities/:id",
    "/jobs", "/jobs/:id", "/customers", "/customers/:name", "/issue-form",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/scheduling",
  ],
  sales_manager: [
    "/", "/command-dashboard", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards",
    "/import/leads-appointments",
    "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id",
    "/customers", "/customers/:name", "/disposition-report", "/reports", "/marketing",
    "/pipeline", "/pipeline/:id", "/commissions", "/marketing-attribution",
    "/follow-ups", "/appointments", "/messages", "/team-messages", "/issues",
    "/pipeline-intelligence", "/scheduling",
  ],
  call_center: [
    "/", "/pipeline", "/pipeline/:id", "/messages", "/team-messages", "/follow-ups",
    "/appointments", "/scheduling",
  ],
  it_staff: [
    "/", "/intake-review", "/pipeline", "/pipeline/:id", "/lead-sources", "/automations", "/lead-integrity",
    "/import/review-queue", "/import/leads-appointments",
    "/messages", "/team-messages", "/opportunities", "/opportunities/:id", "/jobs", "/jobs/:id", "/customers", "/customers/:name",
    "/scheduling", "/scheduling-dispatch", "/closeout", "/permitting", "/ordering-queue", "/install-readiness", "/warehouse", "/inventory",
    "/issues", "/service-tickets", "/contractor-intake", "/contractor-onboarding",
    "/contractors", "/contractors/:id", "/qa-form", "/issue-form",
    "/reports", "/accounting", "/marketing", "/marketing-attribution", "/growth-channels", "/disposition-report", "/alerts", "/intelligence",
    "/portals",
    "/offerings", "/follow-up-engine", "/product-registry", "/vendors",
    "/friction-log", "/settings",
    "/projects", "/projects/:tab",
    "/appointments", "/follow-ups", "/appointment-results", "/appointment-outcome/:appointmentResultId", "/rep-scorecards", "/commissions",
    "/command-dashboard", "/navigation-settings",
    "/execution", "/execution/:tab", "/execution/control-tower", "/execution/timeline", "/execution/summary",
    "/execution/qa-callbacks", "/execution/holdbacks", "/execution/promotion-queue",
    "/execution/installer-network", "/execution/installers", "/execution/vetting/:contractorId",
    "/execution/governance", "/execution/install-today", "/execution/installs-live",
    "/resolve", "/installer-offers", "/checklist-templates",
  ],
};

/**
 * Get all allowed routes for a role based on their permissions.
 * Uses the ROLE_ALLOWED_ROUTES which defines all routes accessible to each role.
 */
export function getAllowedRoutesForRole(role: UserRole): string[] {
  return ROLE_ALLOWED_ROUTES[role] ?? ["/"];
}

/**
 * Match a pathname against the role's allowed route patterns.
 * Handles dynamic segments like /jobs/:id → /jobs/JOB-005
 */
export function isRouteAllowed(role: UserRole, pathname: string): boolean {
  // Use the new permission system
  const allowedRoutes = getAllowedRoutesForRole(role);
  if (!allowedRoutes?.length) return false;

  return allowedRoutes.some((pattern) => {
    // Convert route pattern to regex: /jobs/:id → /jobs/[^/]+
    const regexStr = "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$";
    return new RegExp(regexStr).test(pathname);
  });
}

/**
 * Check if a role has access to a specific route based on its permission key.
 * More granular check using the new permission system.
 */
export function hasRoutePermission(
  role: UserRole,
  pathname: string,
  requiredPermission?: string
): boolean {
  // If a specific permission is required, check it first
  if (requiredPermission) {
    if (!hasPermission(role, requiredPermission)) {
      return false;
    }
  }

  // Then check route access
  return isRouteAllowed(role, pathname);
}
